import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectPopupsCount } from "../../store/selectors/popupSelectors";
import { selectDirection } from "../../store/selectors/appSelectors";
import { DirectionE, LangE } from "../../models/enums";
import { useLazyGetUpdatedTranslationsQuery } from "../../api/translationsAPI";
import { setTranslatedStrings, switchLang } from "../../store/slices/appSlice";

const useApp = () => {
  const dispatch = useAppDispatch();
  const [getUpdatedTranslations, { isFetching }] =
    useLazyGetUpdatedTranslationsQuery();

  // define is popup window opened
  const isPopupOpened = !!useAppSelector(selectPopupsCount);

  // returns blurred background className if popup window is opened
  const blurredBackground = isPopupOpened ? "blur" : "";

  // returns 'ltr' or 'rtl' value
  const direction = useAppSelector(selectDirection);

  // shortcut for checking direction
  const isRtlDirection = direction === DirectionE.RTL;

  const lang = "en";

  const switchAppLang = useCallback(
    async (lang: LangE) => {
      // Parse the (potentially large) translations blob ONCE. The previous
      // implementation JSON.parse'd it three times per language switch, which
      // janked the main thread on switch (spec-08 FE-8).
      const stored =
        JSON.parse(localStorage.getItem("translations")) || {};

      const { data } = await getUpdatedTranslations({
        lang,
        lastUpdateDate: stored?.[lang]?.lastUpdateDate || undefined,
      });

      // The blob as it stands after this call: freshly fetched `data` replaces
      // the current lang's entry when present, otherwise the cached blob stays.
      // (Mirrors the original post-write re-read of localStorage exactly.)
      const updated = data ? { ...stored, [lang]: data } : stored;

      if (data) {
        localStorage.setItem("translations", JSON.stringify(updated));
      }

      dispatch(setTranslatedStrings(data?.consts || updated?.[lang]?.consts));
      dispatch(switchLang(lang));
    },
    [dispatch, getUpdatedTranslations]
  );

  return {
    blurredBackground,
    direction,
    isRtlDirection,
    lang,
    switchAppLang,
    isTranslationsLoading: isFetching,
  };
};

export default useApp;
